import { keepPreviousSearchData } from "@/lib/data/react-query/search/scope";
import { invalidateSearchQueries } from "@/lib/data/react-query/search/invalidation";
import {
  ActionEmptyState,
  AppPageShell,
  Badge,
  Button,
  Card,
  GlobalNotAuthorized,
  Input,
  LoadingSpinner,
  IncidentTypeBadge,
  Typography,
} from "@/components/ui";
import {
  buildMatterStatusGroups,
  LeadStatusTransitionDropdown,
} from "@/components/features/leads/LeadStatusTransitionDropdown";
import { LeadEditAside } from "@/components/features/leads/LeadEditAside";
import { LeadRowContextMenu } from "@/components/features/leads/LeadRowContextMenu";
import { LeadsAssigneeSummary } from "@/components/features/leads/LeadsAssigneeSummary";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { searchMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import {
  getSearchMattersCount,
  getSearchMattersNextPageNumber,
  type SearchMatterStatusCategory,
} from "@/lib/data/requests";
import {
  LEAD_STATUS_CATEGORY_OPTIONS,
  OPPORTUNITY_STATUS_CATEGORY_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getIncidentTypeBadgeVariantFromId } from "@/components/ui/MatterTypeBadge/MatterTypeBadge";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import {
  getMatterSearchResultAssigneeDisplayNames,
  getMatterSearchResultClientName,
} from "@/lib/utils/matters/matter-search-result-utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/_auth/opportunities/")({
  component: RouteComponent,
});

const OPPORTUNITIES_SEARCH_DEBOUNCE_MS = 300;
const OPPORTUNITIES_PAGE_SIZE = 25;

function RouteComponent() {
  const { hasPagePermission, hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<
    string | null
  >(null);
  const [isCreatingOpportunity, setIsCreatingOpportunity] = useState(false);
  const canViewOpportunities = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeOpportunities,
  );
  const debouncedSearchValue = useDebouncedValue(
    searchValue,
    OPPORTUNITIES_SEARCH_DEBOUNCE_MS,
  );
  const matterSearchFilters = useMemo(() => {
    const search = debouncedSearchValue.trim();
    return {
      statusCategories: ["opportunity"] as SearchMatterStatusCategory[],
      ...(search ? { search } : {}),
    };
  }, [debouncedSearchValue]);
  const matterSearchSignature = useMemo(
    () => JSON.stringify(matterSearchFilters),
    [matterSearchFilters],
  );

  useEffect(() => {
    setPageIndex(0);
  }, [matterSearchSignature]);

  const {
    data: opportunitiesData,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    ...searchMattersQueryOptions({
      ...matterSearchFilters,
      pageNumber: pageIndex + 1,
      pageSize: OPPORTUNITIES_PAGE_SIZE,
    }),
    enabled: canViewOpportunities,
    placeholderData: keepPreviousSearchData,
  });
  const nextPageNumber = getSearchMattersNextPageNumber(opportunitiesData);
  const totalCount = getSearchMattersCount(opportunitiesData);
  const pageCount =
    typeof totalCount === "number"
      ? Math.max(1, Math.ceil(totalCount / OPPORTUNITIES_PAGE_SIZE))
      : Math.max(1, nextPageNumber ? pageIndex + 2 : pageIndex + 1);
  const canGoNext = Boolean(nextPageNumber);
  const canGoPrevious = pageIndex > 0;
  const rangeStart = opportunitiesData?.data.length
    ? pageIndex * OPPORTUNITIES_PAGE_SIZE + 1
    : 0;
  const rangeEnd =
    pageIndex * OPPORTUNITIES_PAGE_SIZE + (opportunitiesData?.data.length ?? 0);
  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewOpportunities,
  });
  const matterStatuses = useMemo(
    () => matterSettingsData?.data.statuses ?? [],
    [matterSettingsData],
  );
  const opportunityMatterStatuses = useMemo(
    () =>
      matterStatuses.filter((status) =>
        OPPORTUNITY_STATUS_CATEGORY_OPTIONS.includes(
          status.categoryId as (typeof OPPORTUNITY_STATUS_CATEGORY_OPTIONS)[number],
        ),
      ),
    [matterStatuses],
  );
  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData],
  );
  const matterRoles = useMemo(
    () => matterSettingsData?.data.roles ?? [],
    [matterSettingsData],
  );
  const matterTeams = useMemo(
    () => matterSettingsData?.data.teams ?? [],
    [matterSettingsData],
  );
  const matterStatusGroups = useMemo(
    () => buildMatterStatusGroups(opportunityMatterStatuses),
    [opportunityMatterStatuses],
  );
  const conversionMatterStatusGroups = useMemo(
    () =>
      buildMatterStatusGroups(
        matterStatuses.filter((status) =>
          LEAD_STATUS_CATEGORY_OPTIONS.includes(
            status.categoryId as (typeof LEAD_STATUS_CATEGORY_OPTIONS)[number],
          ),
        ),
      ),
    [matterStatuses],
  );
  const canTransitionMatterStatus = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatusPhase,
    ACTION_PERMISSION_VALUES.create,
  );

  const refreshOpportunitiesListData = useCallback(() => {
    void invalidateSearchQueries(queryClient);
  }, [queryClient]);

  const goToPreviousPage = () => {
    if (!canGoPrevious) {
      return;
    }

    setPageIndex((current) => current - 1);
  };

  const goToNextPage = () => {
    if (!nextPageNumber) {
      return;
    }

    setPageIndex((current) => current + 1);
  };

  const opportunities = useMemo(() => {
    return (opportunitiesData?.data ?? []).filter((row) => {
      const assigneeNames = getMatterSearchResultAssigneeDisplayNames(row);
      if (showUnassignedOnly && assigneeNames.length > 0) {
        return false;
      }

      return true;
    });
  }, [opportunitiesData, showUnassignedOnly]);
  const hasActiveListFilters =
    debouncedSearchValue.trim().length > 0 || showUnassignedOnly;
  const selectedOpportunity = useMemo(() => {
    if (!selectedOpportunityId) {
      return undefined;
    }

    return (opportunitiesData?.data ?? []).find(
      (row) => row.id === selectedOpportunityId,
    );
  }, [opportunitiesData, selectedOpportunityId]);
  const isOpportunityAsideOpen = isCreatingOpportunity || !!selectedOpportunity;
  const opportunityAsideKey = isCreatingOpportunity
    ? "new-opportunity"
    : (selectedOpportunity?.id ?? "closed-opportunity-aside");

  if (!canViewOpportunities) {
    return <GlobalNotAuthorized />;
  }

  return (
    <AppPageShell className="h-full max-h-full gap-6 overflow-hidden">
      <div className="shrink-0 space-y-1">
        <Typography variant="heading" className="text-2xl">
          Opportunities
        </Typography>
      </div>

      <Card className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 flex-1">
          <div
            className={cn(
              "flex min-w-0 shrink-0 grow-0 flex-col transition-[flex-basis] duration-300 ease-in-out",
              isOpportunityAsideOpen ? "basis-full md:basis-1/2" : "basis-full",
            )}
          >
            <div className="flex shrink-0 flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-xs">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  maxLength={200}
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                  }}
                  placeholder="Search opportunities..."
                  className="bg-background h-9 pl-9"
                />
              </div>
              <Button
                size="sm"
                className="self-start md:self-auto"
                onClick={() => {
                  setSelectedOpportunityId(null);
                  setIsCreatingOpportunity(true);
                }}
              >
                <Plus className="h-4 w-4" />
                New opportunity
              </Button>
            </div>

            <div className="flex shrink-0 gap-2 border-b p-4">
              <Button
                size="xs"
                variant={showUnassignedOnly ? "outline" : "default"}
                className="rounded-full px-3"
                onClick={() => {
                  setShowUnassignedOnly(false);
                }}
              >
                All
              </Button>
              <Button
                size="xs"
                variant={showUnassignedOnly ? "default" : "outline"}
                className="rounded-full px-3"
                onClick={() => {
                  setShowUnassignedOnly(true);
                }}
              >
                Unassigned
              </Button>
            </div>

            <div className="relative min-h-0 flex-1 divide-y overflow-y-auto">
              {isLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 px-4 py-8 text-sm">
                  <LoadingSpinner />
                  Loading opportunities...
                </div>
              ) : isError ? (
                <div className="px-4 py-8">
                  <Typography className="text-destructive text-sm">
                    Unable to load opportunities.
                  </Typography>
                </div>
              ) : opportunities.length === 0 ? (
                <div className="flex min-h-full w-full items-center justify-center">
                  <ActionEmptyState
                    variant="plain"
                    title={
                      hasActiveListFilters
                        ? "No opportunities found"
                        : "No opportunities yet"
                    }
                    description={
                      hasActiveListFilters
                        ? "Try adjusting or clearing your search and filters."
                        : "Create an opportunity to start tracking a potential referral."
                    }
                    actions={
                      hasActiveListFilters ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSearchValue("");
                            setShowUnassignedOnly(false);
                          }}
                        >
                          Clear filters
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedOpportunityId(null);
                            setIsCreatingOpportunity(true);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          New opportunity
                        </Button>
                      )
                    }
                  />
                </div>
              ) : (
                opportunities.map((row) => {
                  const matterId = row.id;
                  const assigneeDisplayNames =
                    getMatterSearchResultAssigneeDisplayNames(row);
                  const opportunityName =
                    getMatterSearchResultClientName(row) ||
                    row.title ||
                    "Untitled opportunity";
                  const incidentType = row.incidentType?.label || "Opportunity";
                  const statusLabel = row.status?.label || "Active";
                  const statusId = row.status?.id ?? row.statusId ?? undefined;
                  const isSelected = selectedOpportunityId === row.id;

                  return (
                    <LeadRowContextMenu
                      key={row.id}
                      entityName="opportunity"
                      lead={row}
                      matterRoles={matterRoles}
                      matterStatuses={opportunityMatterStatuses}
                      isMatterStatusesError={isMatterSettingsError}
                      isMatterStatusesLoading={isMatterSettingsLoading}
                      trigger={
                        <div
                          className={cn(
                            "hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center gap-4 px-4 py-4 transition-colors",
                            isSelected &&
                              "border-primary bg-primary/5 border-l-2 pl-3.5",
                          )}
                          onClick={() => {
                            setSelectedOpportunityId(row.id);
                            setIsCreatingOpportunity(false);
                          }}
                        >
                          <div className="border-border bg-background h-5 w-5 shrink-0 rounded border" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <Typography weight="medium" className="truncate">
                                {opportunityName}
                              </Typography>
                              <IncidentTypeBadge
                                variant={getIncidentTypeBadgeVariantFromId(
                                  incidentType,
                                )}
                                className="px-2 py-0 text-[11px]"
                              >
                                {incidentType}
                              </IncidentTypeBadge>
                              {row.claimTypes?.length ? (
                                <span className="text-muted-foreground text-xs">
                                  Claims:{" "}
                                  {row.claimTypes
                                    .map((claim) => claim.label)
                                    .join(", ")}
                                </span>
                              ) : null}
                              <LeadsAssigneeSummary
                                names={assigneeDisplayNames}
                              />
                            </div>
                            <Typography variant="subHeading">
                              {formatDate(row.dateCreated) ?? "No date"}
                            </Typography>
                          </div>
                          <div
                            className={cn(
                              "items-center gap-3 text-sm",
                              isOpportunityAsideOpen
                                ? "hidden xl:flex"
                                : "hidden md:flex",
                            )}
                          >
                            <Typography className="text-muted-foreground">
                              {formatDate(row.dateModified) ?? ""}
                            </Typography>
                            {matterId ? (
                              <LeadStatusTransitionDropdown
                                matterId={matterId}
                                currentStatusId={statusId}
                                currentStatusLabel={statusLabel}
                                matterStatusGroups={matterStatusGroups}
                                canTransitionMatterStatus={
                                  canTransitionMatterStatus
                                }
                                isItemsLoading={isMatterSettingsLoading}
                                isItemsError={isMatterSettingsError}
                              />
                            ) : (
                              <Badge
                                variant="outline"
                                className="border-blue-200 bg-blue-50 text-blue-700"
                              >
                                {statusLabel}
                              </Badge>
                            )}
                          </div>
                        </div>
                      }
                    />
                  );
                })
              )}
            </div>
            <div className="flex shrink-0 flex-col gap-2 border-t px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
              <Typography className="text-muted-foreground text-sm">
                Showing {rangeStart}-{rangeEnd} of{" "}
                {typeof totalCount === "number" ? totalCount : "unknown"}{" "}
                opportunities
                {isFetching && !isLoading ? " (updating...)" : ""}
              </Typography>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goToPreviousPage}
                  disabled={!canGoPrevious || isFetching}
                >
                  Previous
                </Button>
                <Typography className="text-muted-foreground text-sm">
                  Page {pageIndex + 1} of {pageCount}
                </Typography>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goToNextPage}
                  disabled={!canGoNext || isFetching}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>

          <LeadEditAside
            key={opportunityAsideKey}
            isOpen={isOpportunityAsideOpen}
            selectedLead={selectedOpportunity}
            matterStatuses={matterStatuses}
            matterStatusGroups={matterStatusGroups}
            conversionMatterStatusGroups={conversionMatterStatusGroups}
            conversionTarget="lead"
            matterRoles={matterRoles}
            matterTeams={matterTeams}
            incidentTypes={incidentTypes}
            canTransitionMatterStatus={canTransitionMatterStatus}
            isMatterSettingsLoading={isMatterSettingsLoading}
            isMatterSettingsError={isMatterSettingsError}
            intakePipelineCategory="opportunity"
            onClose={() => {
              setSelectedOpportunityId(null);
              setIsCreatingOpportunity(false);
            }}
            refreshLeadsListData={refreshOpportunitiesListData}
          />
        </div>
      </Card>
    </AppPageShell>
  );
}
